import { Checkbox, Typography } from '@material-ui/core'
import React, { FC } from 'react'

import { getTranslate } from '../../localization'
import { Permission, SubPermission } from '../../types/role'
import useStyles from './index.styles'

interface AccessRowProps {
  subPermission: SubPermission
  permission: Permission
  temporaryPermissions: Permission[]
  setTemporayPermissions: (p: Permission[]) => void
}

export const PermissionRow: FC<AccessRowProps> = ({
  subPermission,
  permission,
  temporaryPermissions,
  setTemporayPermissions
}) => {
  const classes = useStyles()

  return (
    <div className={classes.tablerow}>
      <div>
        <Typography variant="h4" className={classes.name}>
          {getTranslate(subPermission.name)}
        </Typography>
      </div>

      {subPermission.actions.map((action) => {
        const isChecked = temporaryPermissions
          .find((perm) => perm.permissionName === permission.permissionName)
          ?.subPermissions.find((subp) => subp.name === subPermission.name)
          ?.actions.find((a) => a.action === action.action)?.selected

        /*
         * An action the resource does not HAVE gets no checkbox at all.
         *
         * The BFF emits a cell for all four actions on every resource and marks the
         * non-existent ones `Disabled: true` with an empty `permissionID`
         * (helpers/iam.go — "Missing action – always shown, not selected, disabled").
         * Rendering those as disabled checkboxes made them indistinguishable from
         * "available but off": an operator ticked Select All, saw empty boxes for
         * ChannelAPIKey/Edit, Logo/Edit, Accommodation/Delete — none of which exist in
         * IAM — and reported that a full-access role could not be created. Select All had
         * in fact granted everything grantable.
         *
         * Keyed on permissionID, NOT on `disabled`: disabled is also set for a real,
         * granted, role-based permission that simply is not editable on this screen. A
         * dash there would claim the permission does not exist, which is the opposite of
         * true.
         */
        if (!action.permissionID) {
          return (
            <div key={action.action} className={classes.unavailableAction}>
              {/* component="span", NOT the default. MUI renders variant="h4" as a real
                  <h4>, so every unavailable cell would become a document heading — and
                  the permission model is sparse enough that a screen-reader user
                  navigating by heading would hit dozens of "Not applicable" headings that
                  title nothing. The variant is wanted for size, the element is not. */}
              <Typography
                variant="h4"
                component="span"
                aria-label={getTranslate('Not applicable')}>
                —
              </Typography>
            </div>
          )
        }

        return (
          <div key={action.action}>
            <Checkbox
              color="primary"
              size="small"
              disabled={action.disabled}
              checked={isChecked}
              onChange={(event) => {
                const checked = event.target.checked

                const updatedPermissions = temporaryPermissions.map((perm) => {
                  if (perm.permissionName !== permission.permissionName) {
                    return perm
                  }

                  return {
                    ...perm,
                    subPermissions: perm.subPermissions.map((subR) => {
                      if (subR.name !== subPermission.name) {
                        return subR
                      }

                      return {
                        ...subR,
                        actions: subR.actions.map((act) =>
                          act.action === action.action
                            ? { ...act, selected: checked }
                            : act
                        )
                      }
                    })
                  }
                })

                setTemporayPermissions(updatedPermissions)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

export default PermissionRow
